import { useCallback, useContext } from 'react';
import { PlaidLinkOnExit, PlaidLinkOptions } from 'react-plaid-link';
import { useQueryClient } from 'react-query';

import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { queries } from '~/constants/queryKeys';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { VinovestNetworkingHelper } from '~/helpers/VinovestNetworkingHelper';
import { useTracking } from '~/hooks/tracking/useTracking';

import { BankAccountProps } from './ConnectBank';

export const useConnectBank = ({
    token,
    setLoading,
    setError,
    onPaymentLinked,
    setIsPlaidComplete
}: BankAccountProps) => {
    const { setDefaultPaymentSourceID, destinationPortfolioId } = useContext(DepositContext);
    const { captureEvent } = useTracking();
    const queryClient = useQueryClient();

    const onSuccess = async (public_token: string, metadata: any) => {
        setLoading(true);
        const { managed } = VinovestNetworkingHelper.getClient();
        const { data: paymentMethod, status } = await managed.createPlaidPaymentIntent(
            public_token,
            metadata.account_id,
            destinationPortfolioId
        );

        if (paymentMethod && status === 200) {
            captureEvent(AnalyticsEventNames.NewPaymentMethodAdded, { method: 'bank' });
            setDefaultPaymentSourceID(paymentMethod?.accountID);
            setLoading(false);
            setIsPlaidComplete(true);

            await queryClient.refetchQueries([queries.getSavedPaymentMethods]);
            onPaymentLinked();
        } else {
            setIsPlaidComplete(true);
            setLoading(false);
            setError('Unable to complete bank account connection, please try again.');
        }
    };

    const onExit = useCallback<PlaidLinkOnExit>(() => {
        setLoading(false);
        setIsPlaidComplete(true);
        setError('Bank account connection was not completed. Please try again.');
    }, []);

    const config: PlaidLinkOptions = {
        token,
        onSuccess,
        onExit
    };

    return { onExit, config, onSuccess };
};
